/**
 * Servicio API para endpoints de OTP
 * HU-FE-04: Funciones tipadas para verificación, reenvío, reset
 */

import { apiClient } from './api';
import type {
    OtpVerifyResponse,
    OtpResendResponse,
    PasswordResetResponse,
} from '../types/otp';

/** Prefijo para las claves de sessionStorage */
const COOLDOWN_KEY_PREFIX = 'otp_cooldown_';
const EMAIL_KEY = 'otp_pending_email';

/**
 * Guarda el cooldown en sessionStorage para persistir si el usuario refresca
 */
function saveCooldown(purpose: string, expiresAt: number): void {
    sessionStorage.setItem(`${COOLDOWN_KEY_PREFIX}${purpose}`, expiresAt.toString());
}

/**
 * Obtiene los segundos restantes de cooldown desde sessionStorage
 */
export function getRemainingCooldown(purpose: string): number {
    const stored = sessionStorage.getItem(`${COOLDOWN_KEY_PREFIX}${purpose}`);
    if (!stored) return 0;

    const expiresAt = parseInt(stored, 10);
    const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));

    if (remaining <= 0) {
        sessionStorage.removeItem(`${COOLDOWN_KEY_PREFIX}${purpose}`);
        return 0;
    }

    return remaining;
}

/**
 * Guarda el email pendiente de verificación
 */
export function savePendingEmail(email: string): void {
    sessionStorage.setItem(EMAIL_KEY, email);
}

/**
 * Obtiene el email pendiente de verificación
 */
export function getPendingEmail(): string | null {
    return sessionStorage.getItem(EMAIL_KEY);
}

/**
 * Limpia el email pendiente
 */
export function clearPendingEmail(): void {
    sessionStorage.removeItem(EMAIL_KEY);
}

export const otpService = {
    // ============================================================
    // Verificación de cuenta
    // ============================================================

    /**
     * Verificar cuenta con código OTP
     */
    verifyAccount: async (email: string, code: string): Promise<OtpVerifyResponse> => {
        return apiClient.post<OtpVerifyResponse>(
            '/api/auth/verify-account',
            { email, code },
            false
        );
    },

    /**
     * Reenviar código de verificación de cuenta
     */
    resendVerification: async (email: string): Promise<OtpResendResponse> => {
        const response = await apiClient.post<OtpResendResponse>(
            '/api/auth/resend-verification',
            { email },
            false
        );

        // Guardar cooldown si hay datos de expiración
        if (response.data?.cooldown_seconds) {
            const expiresAt = Date.now() + response.data.cooldown_seconds * 1000;
            saveCooldown('account_verification', expiresAt);
        }

        return response;
    },

    // ============================================================
    // Recuperación de contraseña
    // ============================================================

    /**
     * Solicitar código de recuperación de contraseña
     */
    forgotPassword: async (email: string): Promise<OtpResendResponse> => {
        const response = await apiClient.post<OtpResendResponse>(
            '/api/auth/forgot-password',
            { email },
            false
        );

        // Guardar cooldown
        if (response.data?.cooldown_seconds) {
            const expiresAt = Date.now() + response.data.cooldown_seconds * 1000;
            saveCooldown('password_reset', expiresAt);
        }

        return response;
    },

    /**
     * Verificar código de recuperación
     */
    verifyResetCode: async (email: string, code: string): Promise<PasswordResetResponse> => {
        return apiClient.post<PasswordResetResponse>(
            '/api/auth/verify-reset-code',
            { email, code },
            false
        );
    },

    /**
     * Cambiar contraseña con token de reset
     */
    resetPassword: async (
        email: string,
        resetToken: string,
        password: string,
        passwordConfirmation: string
    ): Promise<{ message: string }> => {
        return apiClient.post<{ message: string }>(
            '/api/auth/reset-password',
            {
                email,
                reset_token: resetToken,
                password,
                password_confirmation: passwordConfirmation,
            },
            false
        );
    },

    // ============================================================
    // Desvinculación de dispositivo
    // ============================================================

    /**
     * Solicitar desvinculación de dispositivo
     */
    requestUnbind: async (email: string, password: string): Promise<OtpResendResponse> => {
        const response = await apiClient.post<OtpResendResponse>(
            '/api/device/request-unbind',
            { email, password },
            false
        );

        // Guardar cooldown (API nueva usa can_resend_at, legado usa cooldown_seconds)
        if (response.data?.can_resend_at) {
            const expiresAt = Date.parse(response.data.can_resend_at);
            if (!Number.isNaN(expiresAt) && expiresAt > Date.now()) {
                saveCooldown('device_unbind', expiresAt);
            }
        } else if (response.data?.cooldown_seconds) {
            const expiresAt = Date.now() + response.data.cooldown_seconds * 1000;
            saveCooldown('device_unbind', expiresAt);
        }

        return response;
    },

    /**
     * Confirmar desvinculación con código OTP
     */
    confirmUnbind: async (email: string, code: string): Promise<OtpVerifyResponse> => {
        return apiClient.post<OtpVerifyResponse>(
            '/api/device/confirm-unbind',
            { email, code },
            false
        );
    },
};
